import { ADDRESS_PREFIX, addressFieldName } from '../address/postalAddress.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Shared address field descriptors — mirrors `PostalAddress` (spec 0047 §4.1).
 * Molde: `_auditFields.ts`.
 *
 * Without this, every entity that adopts the block would retype nine descriptor
 * entries by hand and they would drift — which is precisely today's problem
 * (`PartyFields.tsx` and `DistributionCenterFields.tsx` disagree on names,
 * limits and which fields exist at all).
 */
/**
 * The 9 address field descriptors for one prefix. Spread into an entity's `fields`.
 *
 * **Always the nine — never a subset per mode (D22).** The mode (§4.2) governs
 * whether `<p>CityId`/`<p>StateId` get *filled*, never whether they exist, so
 * promoting an entity from text to reference is a behaviour change with no
 * schema change.
 *
 * **`audit: 'names-only'` em todos os nove** (spec 0046): o log registra *que* o
 * endereço mudou, sem copiar o valor. O default do descriptor é `'full'`, que
 * gravaria o endereço inteiro no histórico — e endereço de pessoa física é dado
 * pessoal (§5.10). A anotação vive aqui, no gerador, para valer nas cinco
 * entidades de uma vez; antes do bloco ela era repetida campo a campo no
 * `party.descriptor`.
 *
 * **No field is `required`** — an empty address is valid (D16). Which fields a
 * given entity demands *once the user starts filling the block* is the caller's
 * decision, expressed through `validateAddress`'s `requiredKeys`.
 *
 * The array follows the order the user fills the block (§5 / D23) — state
 * before city, because the city list is loaded from the chosen UF.
 *
 * @param prefix Field prefix. Defaults to `'address'`; a second address on the
 *   same entity passes its own (e.g. `'deliveryAddress'`).
 * @param labelPrefix Prepended to every label (`'Entrega — CEP'`). Give the
 *   second block one: two blocks on one entity would otherwise offer two
 *   columns called `CEP` in a grid or a column picker (§4.3).
 */
export function addressFieldDescriptors(prefix = ADDRESS_PREFIX, labelPrefix = '') {
    const name = (key) => addressFieldName(prefix, key);
    const label = (text) => (labelPrefix ? `${labelPrefix} — ${text}` : text);
    return [
        {
            name: name('zipCode'),
            label: label('CEP'),
            type: 'string',
            required: false,
            audit: 'names-only',
            maxLength: FIELD_LIMITS.ZIP_CODE,
            minLength: FIELD_LIMITS.ZIP_CODE,
            pattern: { regex: '^\\d{8}$', message: 'O CEP deve ter 8 dígitos.' },
            description: 'Postal code — 8 digits, stored unmasked (masked only on screen)',
        },
        {
            name: name('street'),
            label: label('Logradouro'),
            type: 'string',
            required: false,
            audit: 'names-only',
            maxLength: FIELD_LIMITS.NAME,
            description: 'Street name, without the number',
        },
        {
            name: name('number'),
            label: label('Número'),
            type: 'string',
            required: false,
            audit: 'names-only',
            maxLength: FIELD_LIMITS.ADDRESS_NUMBER,
            description: 'Street number — string on purpose: "123-A", "s/n", "km 42" (D8)',
        },
        {
            name: name('complement'),
            label: label('Complemento'),
            type: 'string',
            required: false,
            audit: 'names-only',
            maxLength: FIELD_LIMITS.ADDRESS_COMPLEMENT,
            description: 'Apartment, block, room, warehouse bay (D9 — covers "ponto de referência")',
        },
        {
            name: name('district'),
            label: label('Bairro'),
            type: 'string',
            required: false,
            audit: 'names-only',
            maxLength: FIELD_LIMITS.ADDRESS_DISTRICT,
            description: 'District (bairro)',
        },
        {
            name: name('state'),
            label: label('UF'),
            type: 'string',
            required: false,
            audit: 'names-only',
            maxLength: FIELD_LIMITS.UF,
            minLength: FIELD_LIMITS.UF,
            pattern: { regex: '^[A-Z]{2}$', message: 'A UF deve ter 2 letras.' },
            description: 'State abbreviation — written in both modes (D4)',
        },
        {
            name: name('stateId'),
            label: label('Estado (código IBGE)'),
            type: 'string',
            required: false,
            audit: 'names-only',
            description: 'IBGE state code (2 digits) — a **value**, not a reference (D31). Filled in reference mode, empty in text mode',
        },
        {
            name: name('city'),
            label: label('Cidade'),
            type: 'string',
            required: false,
            audit: 'names-only',
            maxLength: FIELD_LIMITS.NAME,
            description: 'Municipality name — written in both modes (D4): a document freezes the day',
        },
        {
            name: name('cityId'),
            label: label('Cidade (código IBGE)'),
            type: 'string',
            required: false,
            audit: 'names-only',
            description: 'IBGE municipality code (7 digits) — a **value**, not a reference (D31). Filled in reference mode, empty in text mode',
        },
    ];
}
/*
 * There is deliberately **no `addressForeignKeys()`** (spec 0047 **D31**).
 *
 * The block used to declare FKs to `City`/`State` so a grid could label the id.
 * Since D27 the ids come from the IBGE and there is no local catalog to point
 * at — and 0257 deleted the two entities outright, at which point a lingering
 * FK declaration would break `entityRegistry.validate()` at bootstrap. It cost
 * little to drop: the FK was already `expand: false`, it only labelled.
 *
 * What holds up every display instead is **D4** — the municipality name and the
 * UF are written next to the ids, always. That stopped being an optimisation
 * and became the only way to render an address without going to the network.
 * Do not loosen it.
 */
//# sourceMappingURL=_addressFields.js.map